import React from 'react';
import {
  CheckCircle2,
  ShoppingCart,
  ExternalLink,
  Hash,
  User,
  Building,
  Calendar,
  ShieldCheck,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { BarcodeViewer } from '../../../../shared/components/BarcodeViewer.js';
import { QrCodeViewer } from '../../../../shared/components/QrCodeViewer.js';
import { BookSynopsisCard } from '../../../../shared/components/BookSynopsisCard.js';
import { BookTableOfContentsCard } from '../../../../shared/components/BookTableOfContentsCard.js';

interface BookVerificationCertificateCardProps {
  book: Book;
  isOwnerOrAdmin: boolean;
  activeCodeTab: 'qr' | 'barcode';
  onSetActiveCodeTab: (tab: 'qr' | 'barcode') => void;
}

export const BookVerificationCertificateCard: React.FC<BookVerificationCertificateCardProps> = ({
  book,
  isOwnerOrAdmin,
  activeCodeTab,
  onSetActiveCodeTab,
}) => {
  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-2xs space-y-4">
      {/* Book Cover + Title & Badges */}
      <div className="flex flex-col sm:flex-row items-start gap-4 pb-4 border-b border-slate-100">
        {/* Book Cover Thumbnail */}
        <div className="shrink-0 self-center sm:self-start">
          <LazyBookCover
            coverUrl={book.coverUrl}
            title={book.title}
            className="w-24 h-36 sm:w-28 sm:h-40 rounded-xl shadow-md border border-slate-200/90 object-cover"
            targetWidth={300}
            quality={85}
            priority
          />
        </div>

        {/* Title, Subtitle, Status & Badges */}
        <div className="flex-1 min-w-0 space-y-2 w-full">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2.5">
            <div className="space-y-1 min-w-0">
              <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-md border border-indigo-100 inline-block">
                {book.category} • Edisi {book.edition || '1'}
              </span>
              <h3 className="text-base sm:text-xl font-bold text-slate-900 leading-snug break-words">
                {book.title}
              </h3>
              {book.subtitle && (
                <p className="text-xs sm:text-sm font-medium text-indigo-700 leading-snug break-words">
                  {book.subtitle}
                </p>
              )}
              {book.originalTitle && (
                <p className="text-xs text-slate-500 italic">
                  Judul Asli: {book.originalTitle}
                </p>
              )}
            </div>

            {/* Status Pill & Optional Purchase Link */}
            <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 shrink-0 pt-1 sm:pt-0">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-2xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="whitespace-nowrap">Terdaftar & Sah</span>
              </div>
              {book.purchaseUrl && (
                <a
                  href={book.purchaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs transition whitespace-nowrap"
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  <span>Beli Buku Resmi</span>
                  <ExternalLink className="w-3 h-3 opacity-80" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Primary Metadata Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-2.5">
          <Hash className="w-4 h-4 text-indigo-600 shrink-0" />
          <div>
            <span className="text-[10px] text-slate-400 block font-medium">Nomor Identifikasi UIBN</span>
            <strong className="font-mono text-slate-900 text-xs">{book.uibn}</strong>
          </div>
        </div>

        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-2.5">
          <User className="w-4 h-4 text-indigo-600 shrink-0" />
          <div>
            <span className="text-[10px] text-slate-400 block font-medium">Penulis / Pengarang</span>
            <strong className="text-slate-900">{book.authorName}</strong>
          </div>
        </div>

        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-2.5">
          <Building className="w-4 h-4 text-indigo-600 shrink-0" />
          <div>
            <span className="text-[10px] text-slate-400 block font-medium">Penerbit Resmi Terdaftar</span>
            <strong className="text-slate-900">{book.publisherName}</strong>
          </div>
        </div>

        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-2.5">
          <Calendar className="w-4 h-4 text-indigo-600 shrink-0" />
          <div>
            <span className="text-[10px] text-slate-400 block font-medium">Tahun Terbit & Bahasa</span>
            <strong className="text-slate-900">{book.publicationYear} • {book.language || 'Indonesia'}</strong>
          </div>
        </div>
      </div>

      {/* Security Verification Stamp Box */}
      <div className="p-3 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 rounded-xl border border-emerald-200/90 text-xs space-y-1">
        <div className="flex items-center justify-between">
          <span className="font-bold text-emerald-900 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Integritas Digital & Stempel Kriptografis
          </span>
          <span className="text-[10px] text-emerald-700 font-mono">
            STATUS: VALID
          </span>
        </div>
        <p className="text-[11px] text-emerald-800 leading-relaxed">
          Naskah buku ini telah memenuhi seluruh kriteria verifikasi standar identifikasi unik buku Indonesia. Catatan revisi dan silsilah edisi tersimpan secara terdesentralisasi dalam registri nasional.
        </p>
      </div>

      {/* Professional Synopsis Overview */}
      {book.synopsis && (
        <BookSynopsisCard
          synopsis={book.synopsis}
          bookTitle={book.title}
          defaultExpanded={false}
          maxCollapsedLength={220}
          showCopy={false}
        />
      )}

      {/* Book Table of Contents (Daftar Isi) */}
      {book.tableOfContents && (
        <BookTableOfContentsCard
          tableOfContents={book.tableOfContents}
          bookTitle={book.title}
          defaultExpanded={false}
          maxCollapsedLength={220}
          showCopy={false}
        />
      )}

      {/* Barcode & QR Code Section */}
      <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">
            Kode Verifikasi Optik
          </span>
          <div className="flex bg-slate-200/80 p-0.5 rounded-lg text-xs">
            <button
              type="button"
              onClick={() => onSetActiveCodeTab('qr')}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition cursor-pointer ${
                activeCodeTab === 'qr' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-600'
              }`}
            >
              QR Code 2D
            </button>
            <button
              type="button"
              onClick={() => onSetActiveCodeTab('barcode')}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition cursor-pointer ${
                activeCodeTab === 'barcode' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-600'
              }`}
            >
              Barcode 1D
            </button>
          </div>
        </div>

        {activeCodeTab === 'qr' ? (
          <QrCodeViewer uibn={book.uibn} bookTitle={book.title} size={140} showDownload={isOwnerOrAdmin} className="w-full" />
        ) : (
          <BarcodeViewer uibn={book.uibn} bookTitle={book.title} showDownload={isOwnerOrAdmin} className="w-full" />
        )}
      </div>
    </div>
  );
};
